import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  linkedSignal,
  model,
  OnDestroy,
  Signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { provideIcons } from '@ng-icons/core';
import { lucideExternalLink, lucideLink2Off } from '@ng-icons/lucide';
import { HlmButton } from '@spartan-ng/helm/button';
import { HlmDialogService } from '@spartan-ng/helm/dialog';
import { toast } from 'ngx-sonner';
import { filter } from 'rxjs';
import { v7 as uuidv7 } from 'uuid';

import { HlmFieldImports } from '@spartan-ng/helm/field';
import { CertificationService } from '../../../../../croisement-référentiel/certification.service';
import { Certification } from '../../../../../croisement-référentiel/models/certification';
import { GetIdFichePipe } from '../../../../../croisement-référentiel/pipes/get-id-fiche.pipe';
import { CertificationStore } from '../../../../../croisement-référentiel/store/certification.store';
import { InterrelationStore } from '../../../../../référentiel-métier/référentiel-metier-interrelations/store/interrelation.store';
import {
  CardCertificationComponent,
  ListActions,
} from '../../../../../shared/components/card-certification/card-certification.component';
import { ConfirmDialogComponent } from '../../../../../shared/components/dialog/confirm-dialog/confirm-dialog.component';
import { EmptyStateComponent } from '../../../../../shared/components/empty-state/empty-state.component';
import { InputSearchComponent } from '../../../../../shared/components/input-search/input-search.component';
import { ToastrErrorComponent } from '../../../../../shared/components/toastr/toastr-error.component';
import {
  URL_FRANCE_COMPETENCES,
  URL_FRANCE_COMPETENCES_RECHERCHE,
} from '../../../../../shared/models/urls-france-compétences';
import { EtapeStore } from '../../../../parcours-etape/store/etape.store';
import { DetailCertificationFormGroup } from '../../detail-form-groups.types';
import { EtapeDetailFormService } from '../../étape-detail-form.service';
import { Référentiel } from '../../../../../administration/gestion-référentiels/models/référentiel.model';

@Component({
  selector: 'app-detail-certification',
  imports: [
    ReactiveFormsModule,
    RouterModule,
    InputSearchComponent,
    EmptyStateComponent,
    HlmButton,
    CardCertificationComponent,
    HlmFieldImports,
  ],
  templateUrl: './detail-certification.component.html',
  providers: [provideIcons({ lucideExternalLink, lucideLink2Off }), GetIdFichePipe],
})
export class DetailCertificationComponent implements OnDestroy {
  protected readonly certificationStore = inject(CertificationStore);
  readonly #etapeStore = inject(EtapeStore);
  readonly #detailFormService = inject(EtapeDetailFormService);
  readonly #certificationStore = inject(CertificationStore);
  readonly #interrelationStore = inject(InterrelationStore);
  readonly #certificationService = inject(CertificationService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #dialog = inject(HlmDialogService);
  readonly #getIdFiche = inject(GetIdFichePipe);

  etape = computed(() => this.#etapeStore.selectedEntity());

  readonly form = input.required<DetailCertificationFormGroup>();

  filtres = model<{ nom: string; niveau?: number | null }>();

  entities = computed(() => this.certificationStore.getPaginated());
  certificationAttachee = linkedSignal<Certification | null>(() => this.#certificationStore.selectedEntity());
  readonly referentiel = input.required<Référentiel>();

  urlFranceCompetences = URL_FRANCE_COMPETENCES;

  readonly certificationAttacheeActions: Signal<ListActions[]> = computed(() => {
    const result: ListActions[] = [
      {
        icon: 'lucideColumns2',
        label: 'Accéder au Croisement des référentiels',
        execute: (certification: Certification) => {
          const url = location.href;
          const fragments = url.split('/');
          fragments.splice(-2);
          fragments.push('croisement', certification.codeFiche);
          window.open(fragments.join('/'), '_blank');
        },
      },
      {
        icon: 'lucideSquareArrowOutUpRight',
        label: 'Accéder à France Compétences',
        execute: (certification?: Certification) =>
          window.open(
            URL_FRANCE_COMPETENCES_RECHERCHE + this.#getIdFiche.transform(certification!.codeFiche),
            '_blank',
          ),
      },
    ];

    if (this.form().enabled) {
      result.push({
        icon: 'lucideLink2Off',
        label: "Détacher la certification de l'étape",
        execute: () => this.detacherCertification(),
      });
    }

    return result;
  });

  readonly certificationDetacheeActions: Signal<ListActions[]> = computed(() => {
    const result: ListActions[] = [
      {
        icon: 'lucideColumns2',
        label: 'Accéder au Croisement des référentiels',
        execute: (certification: Certification) => {
          const url = location.href;
          const fragments = url.split('/');
          fragments.splice(-2);
          fragments.push('croisement', certification.codeFiche);
          window.open(fragments.join('/'), '_blank');
        },
      },
      {
        icon: 'lucideSquareArrowOutUpRight',
        label: 'Accéder à France Compétences',
        execute: (certification: Certification) =>
          window.open(URL_FRANCE_COMPETENCES_RECHERCHE + this.#getIdFiche.transform(certification.codeFiche), '_blank'),
      },
    ];

    if (this.form().enabled) {
      result.push({
        icon: 'lucideLink2',
        label: "Attacher la certification de l'étape",
        execute: (certification: Certification) => this.lierCertification(certification),
      });
    }
    return result;
  });

  constructor() {
    effect(() => {
      const certification = this.etape()?.certification;
      if (!certification) {
        this.certificationAttachee.set(null);
        this.#certificationStore.unselect();
        this.certificationStore.chargerCertifications({ certifications: [] });
      }
    });

    effect(() => {
      const filtres = this.filtres();

      if (filtres) {
        const { nom, niveau } = filtres;

        if (nom.length) {
          this.certificationStore.charger({
            recherche: nom,
            idReferentiel: this.referentiel().id,
            niveau,
          });
        }
      }
    });
  }

  ngOnDestroy(): void {
    toast.dismiss();
  }

  lierCertification(certification: Certification) {
    if (this.form().disabled) return;
    if (!this.etape()) return;
    const toastrId = uuidv7();
    if (this.certificationAttachee() && certification) {
      toast.custom(ToastrErrorComponent, {
        componentProps: {
          message:
            "Pour rattacher la certification, vous devez d'abord détacher la certification actuellement attachée à l'étape.",
          titre: 'Impossible de rattacher une 2ème certification',
          id: toastrId,
        },
        id: toastrId,
      });
      return;
    }
    if (certification) {
      this.#certificationService
        .parCodeFiche(certification.codeFiche, this.referentiel().id)
        .pipe(takeUntilDestroyed(this.#destroyRef))
        .subscribe((certificationComplete) => {
          this.certificationAttachee.set(certificationComplete);
          this.#detailFormService.certification.setValue(certificationComplete, { emitEvent: false });
          this.#detailFormService.type.enable({ emitEvent: false });

          const aptitudes = certificationComplete.blocsCompetences
            .flatMap((x) => x.competences)
            .flatMap((x) => x.aptitudes);

          const interrelations = aptitudes.flatMap((aptitude) =>
            this.#interrelationStore
              .entities()
              .filter((x) => x.idsPhase?.includes(aptitude.idPhase))
              .map((interrelation) => ({ id: `${aptitude.id}-${interrelation.id}` })),
          );

          const productions = aptitudes.flatMap((x) => x.productions ?? []);

          this.#detailFormService.interrelations.setValue([
            ...this.#detailFormService.interrelations.value,
            ...interrelations,
          ]);

          this.#detailFormService.productions.setValue([...this.#detailFormService.productions.value, ...productions]);
        });
    }
  }

  detacherCertification() {
    if (this.form().disabled) return;
    const certification = this.certificationAttachee();
    const etape = this.etape();
    if (!(certification && etape)) return;

    const dataDialog = {
      titre: `Délier la certification '${certification.nom}'`,
      contenu: `Voulez-vous délier la certification '${certification.nom}' de l'étape  '${etape.nom}' ?`,
    };

    const dialogRef = this.#dialog.open(ConfirmDialogComponent, {
      context: dataDialog,
    });

    dialogRef.closed$
      .pipe(
        takeUntilDestroyed(this.#destroyRef),
        filter((result) => result),
      )
      .subscribe(() => {
        this.#detailFormService.certification.setValue(null);
        this.certificationAttachee.set(null);
        this.#certificationStore.unselect();
        this.#detailFormService.type.enable({ emitEvent: false });
        this.#etapeStore.modifierEtape({
          id: etape.id,
          etape: {
            certification: null,
          },
        });
      });
  }
}
